import { groupBy } from 'lodash/fp';
import { ReactElement } from 'react';

import { Template as TemplateDashboard } from 'dashboard/models/template';
import { Theme as ThemeDashboard } from 'dashboard/models/theme';
import { ApiModel } from 'lib/interfaces/api';
import {
  Step,
  StepConfigCompleted,
  StepConfigFailed,
  StepDocumentsUpload,
  UiStepType,
} from 'lib/interfaces/StepsConfig';
import {
  AddressAutocompleteMethod,
  GovernmentIdEnabledIdClassByCountries,
  GovernmentIdEnabledIdClassByCountryEntry,
  GovernmentIdSide,
  SelfieCaptureOption,
  SelfieMatch,
  VerificationConfigDatabase,
  VerificationConfigGovernmentId,
  VerificationConfigSelfie,
} from 'lib/interfaces/verificationConfig';
import { VerificationStatus } from 'lib/verification';
import { getSupportedDocumentCaptureOptions } from 'verify/lib/documents';
import { getSupportedGovernmentIdCaptureOptions } from 'verify/lib/inquiry';
import { SelfieStep } from 'verify/lib/interfaces/selfie';
import { getSupportedSelfieCaptureOptions } from 'verify/lib/selfie';
import { Template as TemplateVerify } from 'verify/models/template';
import { Theme as ThemeVerify } from 'verify/models/theme';
import { DocumentConfigWithSubmitted } from 'verify/redux/selectors/documents';
import VerifyDatabaseView from 'verify/views/VerifyDatabaseView';
import VerifyDocumentsCheckView from 'verify/views/VerifyDocumentsCheckView';
import VerifyDocumentsFailedView from 'verify/views/VerifyDocumentsFailedView';
import VerifyDocumentsIntroView from 'verify/views/VerifyDocumentsIntroView';
import VerifyDocumentsPendingView from 'verify/views/VerifyDocumentsPendingView';
import VerifyDocumentsPromptView from 'verify/views/VerifyDocumentsPromptView';
import VerifyEmailAddressPromptView from 'verify/views/VerifyEmailAddressPromptView';
import VerifyFailedView from 'verify/views/VerifyFailedView';
import VerifyGovernmentIdCheckView from 'verify/views/VerifyGovernmentIdCheckView';
import VerifyGovernmentIdFailedView from 'verify/views/VerifyGovernmentIdFailedView';
import VerifyGovernmentIdPromptView from 'verify/views/VerifyGovernmentIdPromptView';
import VerifyGovernmentIdSelectView from 'verify/views/VerifyGovernmentIdSelectView';
import VerifyIntroView from 'verify/views/VerifyIntroView';
import VerifyPhoneNumberPromptView from 'verify/views/VerifyPhoneNumberPromptView';
import VerifySelectCountryView from 'verify/views/VerifySelectCountryView';
import VerifySelfiePromptView from 'verify/views/VerifySelfiePromptView';
import VerifySuccessView from 'verify/views/VerifySuccessView';

export function generatePages(theme: ThemeDashboard, template: TemplateDashboard): ReactElement[] {
  // NOTE: this is weird, because Verify and Dashboard use different serializers,
  // But to allow interactive-theming, we only use the Dashboard API endpoints.
  const templateCasted = template as TemplateVerify;
  const themeCasted = theme as ThemeVerify;

  const steps: Step[] = template.attributes['steps-config'].steps;
  const groupedSteps = groupBy((step) => step.type, steps);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const mockFunction = (): Promise<void> => undefined;

  const pages = [];

  if (groupedSteps[UiStepType.Start] != null) {
    pages.push(
      <VerifyIntroView handleSubmit={mockFunction} template={templateCasted} theme={themeCasted} />,
    );
  }

  // Only show country select screen with certain verifications.
  if (
    template.attributes['enabled-country-codes'].length !== 1 &&
    groupedSteps[UiStepType.CountrySelect] != null &&
    [ApiModel.VerificationDatabase, ApiModel.VerificationGovernmentId].some(
      (type) => groupedSteps[type] != null,
    )
  ) {
    pages.push(
      <VerifySelectCountryView
        countryCode='US'
        handleContinue={mockFunction}
        handleCountryChange={mockFunction}
        isProcessing={false}
        template={templateCasted}
      />,
    );
  }

  if (groupedSteps[ApiModel.VerificationPhoneNumber] != null) {
    pages.push(
      <VerifyPhoneNumberPromptView
        defaultCountry='US'
        handleSubmit={mockFunction}
        onCountryChange={() => null}
      />,
    );
  }
  if (groupedSteps[ApiModel.VerificationEmailAddress] != null) {
    pages.push(<VerifyEmailAddressPromptView handleSubmit={mockFunction} />);
  }

  if (groupedSteps[ApiModel.VerificationDatabase] != null) {
    const verificationConfigDatabase = (groupedSteps[ApiModel.VerificationDatabase][0].config ??
      {}) as VerificationConfigDatabase;
    const inputFields = verificationConfigDatabase['input-fields'] || [];
    const addressAutocompleteMethod =
      verificationConfigDatabase['address-autocomplete-method'] ||
      AddressAutocompleteMethod.GoogleMaps;
    pages.push(
      <VerifyDatabaseView
        changeFormValue={() => undefined}
        selectedCountryCode='US'
        handleSubmit={mockFunction}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        latLng={undefined}
        inputFields={inputFields}
        isProcessing={false}
        hasSubmittedValues={false}
        addressAutocompleteMethod={addressAutocompleteMethod}
      />,
    );
  }

  if (groupedSteps[ApiModel.VerificationGovernmentId] != null) {
    const documentPromptCaptureButton = template.attributes['document-prompt-capture-button'];
    const verificationConfigGovernmentId = (groupedSteps[ApiModel.VerificationGovernmentId][0]
      .config ?? {}) as VerificationConfigGovernmentId;
    const enabledIdClassesByCountries =
      verificationConfigGovernmentId?.['enabled-id-classes-by-country'] ??
      ({} as GovernmentIdEnabledIdClassByCountries);
    const usEnabledIdClasses = (enabledIdClassesByCountries['us'] ??
      []) as GovernmentIdEnabledIdClassByCountryEntry[];

    pages.push(
      <VerifyGovernmentIdSelectView
        selectedCountryCode='US'
        enabledIdClassesByCountryEntries={usEnabledIdClasses}
        handleRouteToSupport={mockFunction}
        handleSelectType={mockFunction}
        hasEnabledSkipUnknown={false}
        showDisclaimer={false}
      />,
      <VerifyGovernmentIdPromptView
        selectedCountryCode='US'
        currentSide={GovernmentIdSide.Front}
        handleDrop={mockFunction}
        handleSelectCamera={mockFunction}
        handleInputCapture={mockFunction}
        handleDeviceHandoff={mockFunction}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        isProcessing={undefined}
        governmentIdClass='dl'
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        documentPromptCaptureButton={documentPromptCaptureButton}
        enabledCaptureOptions={getSupportedGovernmentIdCaptureOptions(
          verificationConfigGovernmentId,
          'us',
          'dl',
          true,
        )}
      />,
      <VerifyGovernmentIdCheckView
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        currentUpload={{ filename: 'id_filename', fileType: undefined, dataUrl: undefined }}
        handleContinue={mockFunction}
        handleRetry={mockFunction}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        isProcessing={undefined}
      />,
      <VerifyGovernmentIdFailedView
        handleContinue={mockFunction}
        verification={{
          type: ApiModel.VerificationGovernmentId as const,
          attributes: { status: VerificationStatus.Failed },
          relationships: {},
          id: '',
        }}
      />,
    );
  }

  if (groupedSteps[ApiModel.VerificationSelfie] != null) {
    const verificationConfigSelfie = groupedSteps[ApiModel.VerificationSelfie][0]
      .config as VerificationConfigSelfie;

    let enabledCaptureOptions = getSupportedSelfieCaptureOptions({
      verificationConfigSelfie,
      isMobile: false,
      isWebGLIncompatible: false,
      hasCamera: true,
    });
    // If the only enabled option on desktop is device handoff, then assume the intention is to
    // only show selfie on mobile.
    if (
      enabledCaptureOptions.length === 1 &&
      enabledCaptureOptions[0] === SelfieCaptureOption.DeviceHandoff
    ) {
      enabledCaptureOptions = getSupportedSelfieCaptureOptions({
        verificationConfigSelfie,
        isMobile: true,
        isWebGLIncompatible: false,
        hasCamera: true,
      });
    }

    pages.push(
      <VerifySelfiePromptView
        enabledCaptureOptions={enabledCaptureOptions}
        currentStep={SelfieStep.Center}
        handleSelectImage={mockFunction}
        handleDeviceHandoff={mockFunction}
        handleStartRecording={mockFunction}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        isProcessing={undefined}
        requireSelfieMatch={SelfieMatch.ProfileAndCenter}
      />,
    );
  }

  if (groupedSteps[UiStepType.DocumentsUpload] != null) {
    const documentPromptCaptureButton = template.attributes['document-prompt-capture-button'];
    const documentsUploadSteps = groupedSteps[UiStepType.DocumentsUpload] as StepDocumentsUpload[];
    documentsUploadSteps.forEach((documentsUploadStep) => {
      const requiredDocumentConfigs = documentsUploadStep?.config?.required ?? [];
      const introScreenConfig = template.attributes['documents-config']?.['intro-screen'];
      const skipIntroScreen = template.attributes['documents-config']?.['skip-intro-screen'];
      const enabledCaptureOptions = documentsUploadStep?.config?.['enabled-capture-options'] ?? [];
      const allowedDocumentCaptureOptions = getSupportedDocumentCaptureOptions(
        enabledCaptureOptions,
        true,
        false,
      );

      // Only show the intro screen if there is more than one required document for this step.
      // See logic in VerifyDocumentsIntroRoute.
      if (requiredDocumentConfigs.length > 1 && !skipIntroScreen) {
        pages.push(
          <VerifyDocumentsIntroView
            handleContinue={mockFunction}
            isLoading={false}
            isProcessing={false}
            introScreenConfig={introScreenConfig}
            documentConfigs={requiredDocumentConfigs as DocumentConfigWithSubmitted[]}
          />,
        );
      }
      pages.push(
        ...requiredDocumentConfigs.map((documentConfig, index) => (
          <VerifyDocumentsPromptView
            key={index}
            allowedCaptureOptions={allowedDocumentCaptureOptions}
            documentConfig={documentConfig}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            documentPromptCaptureButton={documentPromptCaptureButton}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            isProcessing={undefined}
            handleDrop={mockFunction}
            handleDropRejected={mockFunction}
            handleSelectCamera={mockFunction}
            handleInputCapture={mockFunction}
            handleDeviceHandoff={mockFunction}
            handleSkipDocument={mockFunction}
          />
        )),
      );
    });

    // The following document upload screens are shown for each document and are redundant so show
    // them only once.
    pages.push(
      <VerifyDocumentsCheckView
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        currentUpload={{ filename: 'document_filename', fileType: undefined, dataUrl: undefined }}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        isProcessing={undefined}
        handleContinue={mockFunction}
        handleRetry={mockFunction}
      />,
      <VerifyDocumentsPendingView />,
      <VerifyDocumentsFailedView handleRetry={mockFunction} />,
    );
  }

  pages.push(
    <VerifySuccessView
      handleSubmit={mockFunction}
      hasCompleteButton={true}
      isChildFlow={false}
      isProcessing={false}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pictograph={
        (groupedSteps[UiStepType.Completed]?.[0].config as StepConfigCompleted)?.pictograph
      }
    />,
  );

  pages.push(
    <VerifyFailedView
      handleClose={mockFunction}
      handleSubmitTicket={mockFunction}
      hasCompleteButton={true}
      hasSupport={false}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pictograph={(groupedSteps[UiStepType.Failed]?.[0].config as StepConfigFailed)?.pictograph}
    />,
  );

  return pages;
}
